import React, { useState, useEffect } from "react";
import { useAuth, vortxColors } from '@vortx/shared-ui';
import { useNavigate } from "react-router-dom";
import {
    Paper, TextField, Button, Typography, Box, Alert
} from "@mui/material";

export default function Login() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [isSignup, setIsSignup] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const { login, signup, currentUser } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (currentUser) {
            navigate('/dashboard', { replace: true });
        }
    }, [currentUser, navigate]);

    async function handleSubmit(e) {
        e.preventDefault();
        setError("");
        setLoading(true);

        try {
            if (isSignup) {
                await signup(email, password);
            } else {
                await login(email, password);
            }
            navigate("/dashboard");
        } catch (err) {
            setError(err.message || "Failed to " + (isSignup ? "create account" : "log in"));
        }

        setLoading(false);
    }

    return (
        <Box
            sx={{
                minHeight: 'calc(100vh - 64px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                overflow: 'hidden',
                background: `
                    radial-gradient(ellipse at 50% -10%, rgba(63, 106, 100, 0.35) 0%, transparent 65%),
                    radial-gradient(ellipse at 80% 110%, rgba(178, 140, 80, 0.07) 0%, transparent 45%),
                    radial-gradient(ellipse at 15% 85%, rgba(63, 106, 100, 0.08) 0%, transparent 40%),
                    linear-gradient(180deg, #1a2b28 0%, #131e1c 45%, #0e1614 100%)
                `,
            }}
        >
            {/* Grid pattern overlay */}
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    backgroundImage: `
                        repeating-linear-gradient(
                            0deg,
                            transparent,
                            transparent 79px,
                            rgba(63, 106, 100, 0.045) 79px,
                            rgba(63, 106, 100, 0.045) 80px
                        ),
                        repeating-linear-gradient(
                            90deg,
                            transparent,
                            transparent 79px,
                            rgba(63, 106, 100, 0.045) 79px,
                            rgba(63, 106, 100, 0.045) 80px
                        )
                    `,
                    maskImage: 'radial-gradient(ellipse at 50% 40%, black 20%, transparent 70%)',
                    WebkitMaskImage: 'radial-gradient(ellipse at 50% 40%, black 20%, transparent 70%)',
                    pointerEvents: 'none',
                }}
            />

            {/* Animated gold glow behind card */}
            <Box
                sx={{
                    position: 'absolute',
                    width: 480,
                    height: 480,
                    borderRadius: '50%',
                    background: `radial-gradient(circle, rgba(178, 140, 80, 0.1) 0%, transparent 70%)`,
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    '@keyframes breathe': {
                        '0%, 100%': { opacity: 0.4, transform: 'translate(-50%, -50%) scale(1)' },
                        '50%': { opacity: 1, transform: 'translate(-50%, -50%) scale(1.2)' },
                    },
                    animation: 'breathe 10s ease-in-out infinite',
                    pointerEvents: 'none',
                }}
            />

            {/* Login card */}
            <Paper
                elevation={0}
                sx={{
                    p: { xs: 4, sm: 5 },
                    width: '100%',
                    maxWidth: 400,
                    mx: 2,
                    position: 'relative',
                    zIndex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2.5,
                    borderTop: `2px solid ${vortxColors.gold}`,
                    background: 'linear-gradient(180deg, rgba(36, 56, 51, 0.85) 0%, rgba(26, 26, 26, 0.9) 100%)',
                    backdropFilter: 'blur(20px)',
                    boxShadow: `
                        0 1px 0 0 rgba(63, 106, 100, 0.2),
                        0 16px 48px rgba(0, 0, 0, 0.5),
                        0 0 0 1px rgba(63, 106, 100, 0.1)
                    `,
                    borderRadius: '8px',
                }}
            >
                <Typography
                    variant="h5"
                    align="center"
                    sx={{
                        fontFamily: "'Syne', sans-serif",
                        fontWeight: 600,
                        color: vortxColors.gold,
                        letterSpacing: '0.04em',
                    }}
                >
                    {isSignup ? "Create Account" : "Log In"}
                </Typography>

                {error && <Alert severity="error">{error}</Alert>}

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                    <TextField
                        label="Email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        fullWidth
                    />
                    <TextField
                        label="Password"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        fullWidth
                    />

                    <Button
                        disabled={loading}
                        type="submit"
                        variant="contained"
                        fullWidth
                        sx={{ mt: 0.5, py: 1.3 }}
                    >
                        {isSignup ? "Sign Up" : "Log In"}
                    </Button>
                </form>

                <Button
                    onClick={() => setIsSignup(!isSignup)}
                    sx={{
                        color: vortxColors.stone,
                        fontSize: '0.85rem',
                        '&:hover': { color: vortxColors.gold },
                    }}
                >
                    {isSignup ? "Already have an account? Log In" : "Need an account? Sign Up"}
                </Button>
            </Paper>
        </Box>
    );
}
